/**
 * Roster service — divisions, teams, players, roster entries.
 *
 * Read + write surface for Prompt 04 (import/manual rosters). Tightly
 * coupled to the directory: a player ALWAYS resolves to a Profile.
 */

import type { Division, Id, Player, Profile, Role, RosterEntry, Team } from '../models';
import { getDb, updateDb } from './store';
import { newId, nowIso } from './ids';
import { upsertProfile, createProfile, getProfile, assignRole } from './directoryService';

// --------------------------------------------------------------------------
// Reads
// --------------------------------------------------------------------------

export function listTeams(tournamentId: Id): Team[] {
  return getDb()
    .teams.filter((t) => t.tournamentId === tournamentId)
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function getTeam(id: Id): Team | undefined {
  return getDb().teams.find((t) => t.id === id);
}

export function listDivisions(tournamentId: Id): Division[] {
  return getDb()
    .divisions.filter((d) => d.tournamentId === tournamentId)
    .sort((a, b) => a.rank - b.rank);
}

export function getRosterForTeam(tournamentId: Id, teamId: Id): RosterEntry[] {
  return getDb()
    .rosterEntries.filter((r) => r.tournamentId === tournamentId && r.teamId === teamId)
    .sort((a, b) => a.jerseyNumber - b.jerseyNumber);
}

export function listRosterEntries(tournamentId: Id): RosterEntry[] {
  return getDb().rosterEntries.filter((r) => r.tournamentId === tournamentId);
}

export function getPlayersByIds(ids: ReadonlyArray<Id>): Player[] {
  const set = new Set(ids);
  return getDb().players.filter((p) => set.has(p.id));
}

export function getRosterEntryProfile(entry: RosterEntry): Profile | undefined {
  const player = getDb().players.find((p) => p.id === entry.playerId);
  return player ? getProfile(player.profileId) : undefined;
}

// --------------------------------------------------------------------------
// Division & Team mutations
// --------------------------------------------------------------------------

export interface CreateDivisionInput {
  tournamentId: Id;
  name: string;
  rank?: number;
  bracketLabel?: string;
}

export function createDivision(input: CreateDivisionInput): Division {
  const ts = nowIso();
  const div: Division = {
    id: newId('div'),
    kind: 'division',
    createdAt: ts,
    updatedAt: ts,
    tournamentId: input.tournamentId,
    name: input.name.trim(),
    rank: input.rank ?? listDivisions(input.tournamentId).length + 1,
    bracketLabel: input.bracketLabel,
  };
  updateDb((db) => ({ ...db, divisions: [...db.divisions, div] }));
  return div;
}

export function updateDivision(id: Id, patch: Partial<CreateDivisionInput>): Division | undefined {
  let updated: Division | undefined;
  updateDb((db) => ({
    ...db,
    divisions: db.divisions.map((d) => {
      if (d.id !== id) return d;
      updated = { ...d, ...patch, updatedAt: nowIso() } as Division;
      return updated;
    }),
  }));
  return updated;
}

export function deleteDivision(id: Id): void {
  updateDb((db) => {
    const teamIds = new Set(db.teams.filter((t) => t.divisionId === id).map((t) => t.id));
    return {
      ...db,
      divisions: db.divisions.filter((d) => d.id !== id),
      teams: db.teams.filter((t) => t.divisionId !== id),
      rosterEntries: db.rosterEntries.filter((r) => !teamIds.has(r.teamId)),
      roleAssignments: db.roleAssignments.filter((a) => !teamIds.has(a.teamId ?? '')),
    };
  });
}

const TEAM_PALETTE = ['#7c5cff', '#5cc8ff', '#ff6b9a', '#ffd166', '#5ce0a4', '#ff8c42', '#9b8cff', '#4ad6e1'];

export interface CreateTeamInput {
  tournamentId: Id;
  divisionId: Id;
  name: string;
  shortCode?: string;
  primaryColor?: string;
  secondaryColor?: string;
  logoUrl?: string;
  captainProfileId?: Id;
}

export function createTeam(input: CreateTeamInput): Team {
  const ts = nowIso();
  const existing = getDb().teams.length;
  const team: Team = {
    id: newId('team'),
    kind: 'team',
    createdAt: ts,
    updatedAt: ts,
    tournamentId: input.tournamentId,
    divisionId: input.divisionId,
    name: input.name.trim(),
    shortCode: (input.shortCode ?? input.name.replace(/[^a-zA-Z]/g, '').slice(0, 3).toUpperCase()) || 'TBD',
    primaryColor: input.primaryColor ?? TEAM_PALETTE[existing % TEAM_PALETTE.length],
    secondaryColor: input.secondaryColor,
    logoUrl: input.logoUrl,
    captainProfileId: input.captainProfileId,
  };
  updateDb((db) => ({ ...db, teams: [...db.teams, team] }));
  return team;
}

export function updateTeam(id: Id, patch: Partial<CreateTeamInput>): Team | undefined {
  let updated: Team | undefined;
  updateDb((db) => ({
    ...db,
    teams: db.teams.map((t) => {
      if (t.id !== id) return t;
      updated = { ...t, ...patch, updatedAt: nowIso() } as Team;
      return updated;
    }),
  }));
  return updated;
}

export function deleteTeam(id: Id): void {
  updateDb((db) => ({
    ...db,
    teams: db.teams.filter((t) => t.id !== id),
    rosterEntries: db.rosterEntries.filter((r) => r.teamId !== id),
    roleAssignments: db.roleAssignments.filter((a) => a.teamId !== id),
  }));
}

// --------------------------------------------------------------------------
// Roster entries (player adds/edits/removals)
// --------------------------------------------------------------------------

export interface AddRosterPlayerInput {
  tournamentId: Id;
  teamId: Id;
  /** Display name — used to find/create a profile in the directory. */
  displayName: string;
  email?: string;
  phone?: string;
  jerseyNumber: number;
  position?: string;
  isAlternate?: boolean;
  isCaptain?: boolean;
}

export interface RosterMembershipValidation {
  allowed: boolean;
  message?: string;
  existingEntry?: RosterEntry;
}

export interface RosterMutationResult {
  entry?: RosterEntry;
  created: boolean;
  error?: string;
}

export interface AddRosterProfileInput {
  tournamentId: Id;
  teamId: Id;
  profileId: Id;
  jerseyNumber: number;
  position?: string;
  isAlternate?: boolean;
  isCaptain?: boolean;
  status?: RosterEntry['status'];
}

export interface AddNewRosterPersonInput extends Omit<AddRosterPlayerInput, 'displayName'> {
  displayName: string;
}

export interface UpdateRosterEntryDetailsInput {
  teamId: Id;
  jerseyNumber: number;
  position?: string;
  isAlternate: boolean;
  isCaptain: boolean;
  status: RosterEntry['status'];
}

export function validateRosterMembership(input: {
  tournamentId: Id;
  teamId: Id;
  playerId: Id;
  excludeRosterEntryId?: Id;
}): RosterMembershipValidation {
  const db = getDb();
  const targetTeam = db.teams.find((team) => team.id === input.teamId && team.tournamentId === input.tournamentId);
  if (!targetTeam) return { allowed: false, message: 'Choose a valid team in this event.' };

  const existing = db.rosterEntries.find((entry) => {
    if (entry.id === input.excludeRosterEntryId) return false;
    if (entry.tournamentId !== input.tournamentId || entry.playerId !== input.playerId) return false;
    const team = db.teams.find((row) => row.id === entry.teamId);
    return team?.divisionId === targetTeam.divisionId;
  });

  if (!existing) return { allowed: true };

  const profile = getRosterEntryProfile(existing);
  const existingTeam = db.teams.find((team) => team.id === existing.teamId);
  const division = db.divisions.find((row) => row.id === targetTeam.divisionId);
  return {
    allowed: false,
    existingEntry: existing,
    message: `${profile?.displayName ?? 'This person'} is already rostered on ${existingTeam?.name ?? 'another team'} in ${division?.name ?? 'this division'}.`,
  };
}

export function addNewRosterPerson(input: AddNewRosterPersonInput): RosterMutationResult {
  const profile = createProfile({
    displayName: input.displayName,
    email: input.email,
    phone: input.phone,
    defaultRole: input.isCaptain ? 'captain' : 'player',
    globalRoles: input.isCaptain ? ['captain', 'player'] : ['player'],
    source: 'manual',
  });
  return addRosterProfile({ ...input, profileId: profile.id });
}

export function addRosterPlayerResult(input: AddRosterPlayerInput): RosterMutationResult {
  const profile = upsertProfile({
    displayName: input.displayName,
    email: input.email,
    phone: input.phone,
    defaultRole: input.isCaptain ? 'captain' : 'player',
    globalRoles: input.isCaptain ? ['captain', 'player'] : ['player'],
    source: 'manual',
  });
  return addRosterProfile({ ...input, profileId: profile.id });
}

export function addRosterProfile(input: AddRosterProfileInput): RosterMutationResult {
  const profile = getProfile(input.profileId);
  if (!profile) return { created: false, error: 'Choose a valid Directory person.' };

  const player = ensurePlayerForProfile(profile, input.jerseyNumber, input.position);
  const existing = getDb().rosterEntries.find(
    (r) => r.tournamentId === input.tournamentId && r.teamId === input.teamId && r.playerId === player.id,
  );
  if (existing) {
    syncRosterRoles({
      tournamentId: input.tournamentId,
      teamId: input.teamId,
      profileId: profile.id,
      isCaptain: input.isCaptain ?? false,
    });
    return { entry: existing, created: false };
  }

  const validation = validateRosterMembership({
    tournamentId: input.tournamentId,
    teamId: input.teamId,
    playerId: player.id,
  });
  if (!validation.allowed) return { created: false, error: validation.message };

  const ts = nowIso();
  const entry: RosterEntry = {
    id: newId('roster'),
    kind: 'roster-entry',
    createdAt: ts,
    updatedAt: ts,
    tournamentId: input.tournamentId,
    teamId: input.teamId,
    playerId: player.id,
    jerseyNumber: normalizeJersey(input.jerseyNumber),
    position: cleanString(input.position),
    isAlternate: input.isAlternate ?? false,
    status: input.status ?? 'pending',
  };
  updateDb((db) => ({ ...db, rosterEntries: [...db.rosterEntries, entry] }));

  syncRosterRoles({
    tournamentId: input.tournamentId,
    teamId: input.teamId,
    profileId: profile.id,
    isCaptain: input.isCaptain ?? false,
  });

  return { entry, created: true };
}

export function addRosterPlayer(input: AddRosterPlayerInput): RosterEntry {
  const result = addRosterPlayerResult(input);
  if (!result.entry) throw new Error(result.error ?? 'Unable to add roster player.');
  return result.entry;
}

function ensurePlayerForProfile(profile: Profile, jerseyNumber: number, position?: string): Player {
  let player = getDb().players.find((p) => p.profileId === profile.id);
  if (!player) {
    const ts = nowIso();
    player = {
      id: newId('player'),
      kind: 'player',
      createdAt: ts,
      updatedAt: ts,
      profileId: profile.id,
      defaultJerseyNumber: normalizeJersey(jerseyNumber),
      position: cleanString(position),
    };
    const created = player;
    updateDb((db) => ({ ...db, players: [...db.players, created] }));
  } else if (!player.defaultJerseyNumber || !player.position) {
    const patch: Partial<Player> = {};
    if (!player.defaultJerseyNumber) patch.defaultJerseyNumber = normalizeJersey(jerseyNumber);
    if (!player.position && cleanString(position)) patch.position = cleanString(position);
    if (Object.keys(patch).length > 0) {
      const updatedAt = nowIso();
      updateDb((db) => ({
        ...db,
        players: db.players.map((row) => (row.id === player!.id ? { ...row, ...patch, updatedAt } : row)),
      }));
      player = { ...player, ...patch, updatedAt };
    }
  }
  return player;
}

export function updateRosterEntryDetails(id: Id, input: UpdateRosterEntryDetailsInput): RosterMutationResult {
  const current = getDb().rosterEntries.find((entry) => entry.id === id);
  if (!current) return { created: false, error: 'Roster entry not found.' };

  const validation = validateRosterMembership({
    tournamentId: current.tournamentId,
    teamId: input.teamId,
    playerId: current.playerId,
    excludeRosterEntryId: id,
  });
  if (!validation.allowed) return { created: false, error: validation.message };

  const profile = getRosterEntryProfile(current);
  const previousTeamId = current.teamId;
  let updated: RosterEntry | undefined;
  updateDb((db) => ({
    ...db,
    rosterEntries: db.rosterEntries.map((entry) => {
      if (entry.id !== id) return entry;
      updated = {
        ...entry,
        teamId: input.teamId,
        jerseyNumber: normalizeJersey(input.jerseyNumber),
        position: cleanString(input.position),
        isAlternate: input.isAlternate,
        status: input.status,
        updatedAt: nowIso(),
      };
      return updated;
    }),
  }));

  if (profile && updated) {
    syncRosterRoles({
      tournamentId: updated.tournamentId,
      teamId: updated.teamId,
      previousTeamId,
      profileId: profile.id,
      isCaptain: input.isCaptain,
    });
  }

  return updated ? { entry: updated, created: false } : { created: false, error: 'Roster entry not found.' };
}

export function updateRosterEntry(
  id: Id,
  patch: Partial<Omit<RosterEntry, 'id' | 'kind' | 'createdAt' | 'tournamentId' | 'playerId'>>,
): RosterEntry | undefined {
  let updated: RosterEntry | undefined;
  updateDb((db) => ({
    ...db,
    rosterEntries: db.rosterEntries.map((r) => {
      if (r.id !== id) return r;
      updated = { ...r, ...patch, updatedAt: nowIso() } as RosterEntry;
      return updated;
    }),
  }));
  return updated;
}

export function removeRosterEntry(id: Id): void {
  const current = getDb().rosterEntries.find((entry) => entry.id === id);
  const profile = current ? getRosterEntryProfile(current) : undefined;
  updateDb((db) => ({
    ...db,
    rosterEntries: db.rosterEntries.filter((r) => r.id !== id),
  }));
  if (current && profile) {
    syncRosterRoles({
      tournamentId: current.tournamentId,
      teamId: current.teamId,
      profileId: profile.id,
      isCaptain: false,
      removePlayerIfNoRoster: true,
    });
  }
}

function syncRosterRoles(input: {
  tournamentId: Id;
  teamId: Id;
  previousTeamId?: Id;
  profileId: Id;
  isCaptain: boolean;
  removePlayerIfNoRoster?: boolean;
}): void {
  assignRole({
    profileId: input.profileId,
    tournamentId: input.tournamentId,
    teamId: input.teamId,
    role: 'player' as Role,
  });

  if (input.isCaptain) {
    assignRole({
      profileId: input.profileId,
      tournamentId: input.tournamentId,
      teamId: input.teamId,
      role: 'captain' as Role,
    });
  }

  updateDb((db) => {
    const touchedTeamIds = new Set([input.teamId, input.previousTeamId].filter(Boolean) as Id[]);
    const remainingRosterTeamIds = new Set(
      db.rosterEntries
        .filter((entry) => {
          if (entry.tournamentId !== input.tournamentId) return false;
          const player = db.players.find((row) => row.id === entry.playerId);
          return player?.profileId === input.profileId;
        })
        .map((entry) => entry.teamId),
    );

    return {
      ...db,
      teams: db.teams.map((team) => {
        if (!touchedTeamIds.has(team.id)) return team;
        if (team.id === input.teamId && input.isCaptain) {
          return { ...team, captainProfileId: input.profileId, updatedAt: nowIso() };
        }
        if (team.captainProfileId === input.profileId) {
          return { ...team, captainProfileId: undefined, updatedAt: nowIso() };
        }
        return team;
      }),
      roleAssignments: db.roleAssignments.filter((assignment) => {
        if (
          input.isCaptain &&
          assignment.tournamentId === input.tournamentId &&
          assignment.teamId === input.teamId &&
          assignment.role === 'captain' &&
          assignment.profileId !== input.profileId
        ) {
          return false;
        }
        if (
          assignment.profileId !== input.profileId ||
          assignment.tournamentId !== input.tournamentId ||
          !touchedTeamIds.has(assignment.teamId ?? '')
        ) {
          return true;
        }
        if (assignment.role === 'captain') return assignment.teamId === input.teamId && input.isCaptain;
        if (assignment.role === 'player' && input.removePlayerIfNoRoster && !remainingRosterTeamIds.has(assignment.teamId ?? '')) {
          return false;
        }
        if (assignment.role === 'player' && input.previousTeamId && assignment.teamId === input.previousTeamId && !remainingRosterTeamIds.has(input.previousTeamId)) {
          return false;
        }
        return true;
      }),
    };
  });
}

function normalizeJersey(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.round(value));
}

function cleanString(value?: string): string | undefined {
  const out = value?.trim();
  return out || undefined;
}
